import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { omit } from 'lodash-es';
import type { CartItem, CartState } from '../../types';

const initialState: CartState = {
  cartItems: {},
  selectedItem: '',
  partialItems: {
    cartItems: {},
    group_key: null,
    price_algo_max: false,
    number_of_pieces: null,
  },
};

// Helper: add addon to cart items
function addAddonToItems(
  targetUUID: string,
  addonItem: CartItem,
  items: Record<string, CartItem>
): Record<string, CartItem> {
  let parentUUID = targetUUID;
  if (!(parentUUID in items)) {
    const keys = Object.keys(items);
    if (keys.length === 0) return items;
    parentUUID = keys[keys.length - 1];
  }
  const parent = { ...items[parentUUID], addons: [...(items[parentUUID].addons || []), addonItem] };
  return { ...items, [parentUUID]: parent };
}

// Helper: remove addon from cart items
function removeAddonFromItems(
  parentUUID: string,
  addonUUID: string,
  items: Record<string, CartItem>
): Record<string, CartItem> {
  const parent = items[parentUUID];
  if (!parent || !parent.addons) return items;
  const newAddons = parent.addons.filter(a => a.uuid !== addonUUID);
  return { ...items, [parentUUID]: { ...parent, addons: newAddons } };
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItem(state, action: PayloadAction<CartItem>) {
      state.cartItems[action.payload.uuid] = action.payload;
    },
    addAddon(state, action: PayloadAction<{ item: CartItem; targetUUID: string }>) {
      state.cartItems = addAddonToItems(action.payload.targetUUID, action.payload.item, state.cartItems);
    },
    removeItem(state, action: PayloadAction<string>) {
      state.cartItems = omit(state.cartItems, action.payload) as Record<string, CartItem>;
      if (state.selectedItem === action.payload) state.selectedItem = '';
    },
    removeItemByUUID(state, action: PayloadAction<string>) {
      state.cartItems = omit(state.cartItems, action.payload) as Record<string, CartItem>;
      if (state.selectedItem === action.payload) state.selectedItem = '';
    },
    removeAddon(state, action: PayloadAction<{ addonUUID: string; parentUUID: string }>) {
      state.cartItems = removeAddonFromItems(action.payload.parentUUID, action.payload.addonUUID, state.cartItems);
    },
    selectItem(state, action: PayloadAction<string>) {
      state.selectedItem = state.selectedItem === action.payload ? '' : action.payload;
    },
    // Partial items (pola-pola)
    addPart(state, action: PayloadAction<CartItem>) {
      state.partialItems.cartItems[action.payload.uuid] = action.payload;
      state.partialItems.group_key = action.payload.group_id ?? null;
      state.partialItems.price_algo_max = action.payload.price_algorithm_max ?? false;
      state.partialItems.number_of_pieces = action.payload.number_of_pieces ?? null;
    },
    removePart(state, action: PayloadAction<string>) {
      state.partialItems.cartItems = omit(state.partialItems.cartItems, action.payload) as Record<string, CartItem>;
      if (Object.keys(state.partialItems.cartItems).length === 0) {
        state.partialItems.group_key = null;
        state.partialItems.price_algo_max = false;
        state.partialItems.number_of_pieces = null;
      }
    },
    addPartAddon(state, action: PayloadAction<{ item: CartItem; targetUUID: string }>) {
      state.partialItems.cartItems = addAddonToItems(
        action.payload.targetUUID, action.payload.item, state.partialItems.cartItems
      );
    },
    removePartAddon(state, action: PayloadAction<{ addonUUID: string; parentUUID: string }>) {
      state.partialItems.cartItems = removeAddonFromItems(
        action.payload.parentUUID, action.payload.addonUUID, state.partialItems.cartItems
      );
    },
    resetParts(state) {
      state.partialItems = initialState.partialItems;
    },
    checkout() {
      return initialState;
    },
    resetCart() {
      return initialState;
    },
  },
});

export const {
  addItem, addAddon, removeItem, removeItemByUUID, removeAddon, selectItem,
  addPart, removePart, addPartAddon, removePartAddon, resetParts,
  checkout, resetCart,
} = cartSlice.actions;

export default cartSlice.reducer;
